import http from '@/services/http'
import { goodsUrl } from '@/services/url'
import type { SkuListReqDTO, SkusGetReq, SkusGetRes, SkusListRes } from './type'
import type * as Types from './type'
import { batchCallApi } from '@/utils/func'
import { businessTimezoneTranslate } from '../businessTimezone'

interface GoodsListRes<T> {
  list: T
  pageNum: number
  pageSize: number
  total: number
}

/** 创建商品 */
export const createGoodsApi = async (data) => await http.admin.post(goodsUrl.createGoods, data)
/** 删除商品 */
export const deleteGoodsApi = async (data) => await http.admin.post(goodsUrl.deleteGoods, data)
/** 更新商品 */
export const updateGoodsApi = async (data) => await http.admin.post(goodsUrl.updateGoods, data)
/**查询商品列表 */
export const searchGoodsApi = async (data) => await http.admin.post<GoodsListRes<any>>(goodsUrl.searchGoods, data)

/** 查询商品详情列表 */
export const getSkusList = async (data: SkuListReqDTO) => await http.admin.post<SkusListRes>('/api/skus/list', data)

/**批量更新商品 */
export const batchUpdateGoods = (data: Types.BatchUpdateReq) => http.admin.post('/api/skus/batch-update', data)

/**批量删除商品 */
export const batchDeleteGoods = async (data) => await http.admin.post(goodsUrl.batchDeleteGoods, data)

/**查询商品详情 */
export const getGoodDetailApi = async (params: SkusGetReq) => await http.admin.get<SkusGetRes>('/api/skus/get', { params })

/** 校验id是否可用 */
export const checkSkusId = async (params) => await http.admin.get(goodsUrl.skusCheckId, { params })

type checkBarcodeQuery = { skuId: string; barcode: string }[]

/** 校验商品条码是否存在 */
export const checkBarcodeIsExist = async (list: checkBarcodeQuery) => {
  const barcodes = list.map((item) => item.barcode).filter(Boolean)
  if (!barcodes.length) {
    return {
      result: {},
      allNotExist: true,
      skuResult: {},
    }
  }
  const res = await http.admin.post<Types.ExistBarcodesRes>('/api/skus/exist-barcodes', {
    barcodes,
  })

  // 检查参数内是否有重复的条码
  list.forEach(({ barcode }) => {
    const skuIdList = res.data.result[barcode] || []
    const itemList = list.filter((i) => i.barcode === barcode)
    if (itemList.length > 1) {
      itemList.forEach((i) => {
        skuIdList.push(i.skuId)
      })
      res.data.allNotExist = false
      res.data.result[barcode] = Array.from(new Set(skuIdList))
    }
  })

  const skuResult = list.reduce((p, c) => {
    const skuIds = res.data.result[c.barcode] || []
    // 如果没有对应的sku，则不存在重复
    if (!skuIds.length) p[c.skuId] = false
    // 如果存在，但是sku是当前商品，也认为是不存在重复
    else if (skuIds.length === 1 && skuIds[0] === c.skuId) p[c.skuId] = false
    // 如果存在，并且sku不是当前商品，则存在重复
    else p[c.skuId] = true
    return p
  }, {} as Record<string, boolean>)
  return { ...res.data, skuResult } as Types.ExistBarcodesResult
}

export const batchCallCheckBarcodeIsExist = batchCallApi(checkBarcodeIsExist, {
  wait: 500,
  handleData: (lastDataList: [checkBarcodeQuery] | [], newDataList: [checkBarcodeQuery]) => {
    const cacheList = (lastDataList[0] || []).concat(newDataList[0])
    const skuIdMap = {}
    const newList: checkBarcodeQuery = []
    // 去重，取最后一个
    cacheList.reverse().forEach((i) => {
      if (skuIdMap[i.skuId]) return
      skuIdMap[i.skuId] = true
      newList.push(i)
    })
    return [newList] as [checkBarcodeQuery]
  },
})

/** 获取oss上传地址及参数 */
export const getUploadTargetApi = async (params) =>
  await http.admin.get<{
    actionUrl: string
    fields?: { key: string; value: string }[]
    headers?: { [key: string]: string }
  }>(goodsUrl.getUploadTarget, { params })
/**批量导入 */
export const batchImportApi = async (data) => await http.admin.post(goodsUrl.batchImport, data)
/**检验是否可以删除 */
export const validateDeleteApi = async (data) => await http.admin.post(goodsUrl.validateDelete, data)
/**删除商品 */
export const deleteGoodApi = async (data) => await http.admin.post(goodsUrl.deleteGood, data)

/**
 * 同步商品
 */
interface ISkuSync {
  source: string
  storeIds: string[]
  syncSku?: boolean
  syncSkuMapping?: boolean
}

interface IGoodsStatusParams {
  language?: string
  skuId: string
  enabled: boolean
}

interface IBatchGoodsStatusParams {
  language?: string
  skuIds: string[]
  enabled: boolean
}

export const postSkuSync = (data: ISkuSync): Promise<any> => http.admin.post(goodsUrl.orderSync, data)

/** 停用/启用商品 */
export const setGoodsStatusApi = async (data: IGoodsStatusParams) => await http.admin.post(goodsUrl.setGoodsStatus, data)
/** 批量停用/启用商品 */
export const batchSetGoodsStatusApi = async (data: IBatchGoodsStatusParams) => await http.admin.post(goodsUrl.batchSetGoodsStatus, data)

/** 查询商品列表 */
export const getSkuGruopSearch = (data: Types.SpuSkuGroupSearchReq) => http.admin.post<Types.SpuSkuGroupSearchRes>('/api/spu-sku-group/search', data)

export const getOSSUpload = async (params: { uploadSceneId: string; suffix: string; fileName: string }) => {
  const res = await http.admin.get<{
    actionUrl: string
    fields?: { key: string; value: string }[]
    headers?: { key: string; value: string }[]
  }>('/api/tasks/sign', { params })

  const formData = new FormData()
  res.data.fields?.forEach((item) => {
    formData.append(item.key, item.value)
  })

  const headers = {} as Record<string, string>
  res.data.headers?.forEach((item) => {
    headers[item.key] = item.value
  })

  const accessUrl = process.env.CND_URL + '/' + formData.get('key')

  return {
    ...res.data,
    formData,
    headers,
    accessUrl,
  }
}

/** 上传到亚马逊 */
export const upload2OSS = async (params: { url: string; formData: any; headers: any; onUploadProgress?: (e: ProgressEvent) => void }) => {
  const response = await http.post(params.url, params.formData, {
    headers: { ...params.headers },
    onUploadProgress: params?.onUploadProgress,
    timeout: 60e3,
    withCredentials: false,
  })
  const { status } = response
  if (status === 204) {
    return {
      bizCode: 'SUCCESS',
      data: response,
    }
  }

  return response
}

export const fetchSkuPlatformMapping = businessTimezoneTranslate(
  (data: Types.FetchSkuPlatformMappingReq) => http.admin.post<Types.FetchSkuPlatformMappingRes>('/api/sku-platform-mapping/page', data),
  [],
  ['data.list.[].createTime']
)
